import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import AlertDialog from "@components/ui/ConfirmDialogue";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import { useLocation, useNavigate } from "react-router-dom";
import NotFound from "@components/ui/NotFound";
import { useUserData } from "context/OAuth/AuthContext";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import { sleepCoachOptions } from "data/dropdowndData/SleepCoachDropdowns";
import {
  createSleepPlan,
  fetchSleepSteps,
  submitSleepEntries,
} from "api/sleepCoach";
import { useTranslation } from "react-i18next";
import MedicalViewMultipSelectDropdown from "@components/ui/customDropdowns/MedicalViewMultipSelectDropdown";

const SleepCoachPreferencesSelection = () => {
  const { subscription } = useUserData();
  const location = useLocation();
  const navigate = useNavigate();
  if (!location?.state) {
    navigate(-1);
  }
  const isReacreate = location?.state?.isReacreate;
  const [selectedTags, setSelectedTags] = useState({});
  const options = sleepCoachOptions;
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFieldsLoading, setIsFieldsLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();

  const validateInput = (key, value) => {
    if (
      key === "dailyCalorieAdjustment" ||
      key === "targetDailyCalories" ||
      key === "maintenanceCalories"
    ) {
      return null;
    } else {
      if (!value) {
        return "This field is required";
      }

      if (value < 1) {
        return "Value must be greater than 0.";
      }
      if (isNaN(value)) {
        return "Please enter a valid number";
      }

      if (value < 0) {
        return "Value cannot be negative";
      }

      return null; // No error
    }
  };

  const handleClickOpen = () => {
    // First validate all numeric fields
    const newErrors = {};

    options.forEach(({ key, type }) => {
      const fieldLabel = t(`sleepCoachPreferences.fields.${key}`);
      if (type === "numeric") {
        const error = validateInput(key, selectedTags[key]);
        if (error) {
          newErrors[key] = error;
        }
      }

      // Keep your existing validations for other field types
      if (
        type === "multi-select" &&
        (!selectedTags[key] || selectedTags[key].length === 0)
      ) {
        newErrors[key] = "Please select at least one option";
      } else if (type === "select" && !selectedTags[key]) {
        newErrors[key] = t("validation.required", {
          field: fieldLabel,
        });
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setOpen(true);
    } else {
      toast.error(t("toast.OopsReview"));
    }
  };
  const handleClose = (key) => {
    if (key === "cancel") {
      setOpen(false);
    } else {
      handleSavePreferences();
      setOpen(false);
    }
  };
  const handleSavePreferences = async () => {
    setIsLoading(true);
    try {
      await submitSleepEntries(selectedTags);
      await createSleepPlan();

      toast.info(t("toast.informationSubmit"));
      navigate("/sleep-coach-dashboard", {
        state: {
          sleepCoach: true,
        },
      });
    } catch (error) {
      console.log("Error submitting details", error);
      toast.error(t("toast.failedSubmit"));
    } finally {
      setIsLoading(true);
    }
  };

  useEffect(() => {
   if (subscription?.status === "active" || subscription?.status === "pending-cancel") {
      fetchStepsData();
    } else {
      navigate("/");
    }
  }, []);

  const fetchStepsData = async () => {
    try {
      const { data } = await fetchSleepSteps();
      if (data) {
        setSelectedTags(data);
      } else {
        navigate("/sleep-coach");
      }
    } catch (error) {
      console.error(
        "Error fetching diet plan:",
        error.response ? error.response.data : error.message
      );
    } finally {
      setIsFieldsLoading(false);
    }
  };

  useEffect(() => {
    fetchStepsData();
  }, []);

  const removeTag = (key, tag) => {
    const updated = selectedTags[key].filter((t) => t !== tag);

    if (updated.length === 0) {
      setErrors((prev) => ({ ...prev, [key]: true }));
    }

    setSelectedTags((prev) => ({
      ...prev,
      [key]: updated,
    }));
  };

  const handleSelectChange = (key, value) => {
    setSelectedTags((prev) => ({ ...prev, [key]: value }));

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (!value || value === "") {
        updatedErrors[key] = true;
      } else {
        delete updatedErrors[key];
      }
      return updatedErrors;
    });
  };

  const handleMultiSelectChange = (key, values) => {
    if (
      key === "healthConditions" ||
      key === "foodAllergies" ||
      key === "exoticDishes"
    ) {
      const lastSelected = values[values.length - 1];

      if (lastSelected === "None") {
        values = ["None"];
      } else {
        values = values.filter((v) => v !== "None");
      }
    }
    if (key === "selectableDays") {
      const lastSelected = values[values.length - 1];

      if (lastSelected === "All Days") {
        values = ["All Days"];
      } else {
        values = values.filter((v) => v !== "All Days");
      }
    }
    setSelectedTags((prev) => ({ ...prev, [key]: values }));

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (!values || values.length === 0) {
        updatedErrors[key] = true;
      } else {
        delete updatedErrors[key];
      }
      return updatedErrors;
    });
  };

  if (location?.state) {
    return (
      <>
        {isFieldsLoading ? (
          <ActualDietPlanLoader sleepCoach={true} />
        ) : (
          <>
            <div className="bg-white shadow-lg p-4 md:p-6 my-4 md:my-6 rounded-lg min-h-[420px] lg:min-h-[400px]">
              {/* Header Section */}
              <div className="text-center mb-6 md:mb-8">
                <h1 className="text-lg md:text-xl font-semibold text-gray-800 mb-3 md:mb-4">
                  {t("sleepCoachPreferences.Review & Confirm Your Preferences")}
                </h1>
                <div className="space-y-2 text-sm md:text-base text-gray-600 max-w-3xl mx-auto">
                  <p>
                    {t(
                      "sleepCoachPreferences.Your personalized sleep plan will be created based on these preferences."
                    )}
                  </p>
                  <p>
                    {t(
                      "sleepCoachPreferences.Please note, your sleep plan will be created once every week."
                    )}
                  </p>
                  <p>
                    {t(
                      "sleepCoachPreferences.Make sure everything looks right before confirming."
                    )}
                  </p>
                </div>
              </div>

              {/* Preferences Table */}
              <div className="border border-gray-200 rounded-lg shadow-sm">
                {/* Table Header - Hidden on mobile, shown on desktop */}
                <div className="hidden md:grid bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white p-4 font-semibold">
                  <div className="grid grid-cols-8 gap-4">
                    <span className="col-span-4">
                      {t("preferences.headers.options")}
                    </span>
                    <span className="col-span-2">
                      {t("preferences.headers.preferences")}
                    </span>
                    <span className="col-span-2">
                      {t("preferences.headers.selected")}
                    </span>
                  </div>
                </div>

                {/* Mobile Header */}
                <div className="md:hidden bg-gradient-to-r from-blue-600 to-blue-400 text-white p-3 font-semibold text-center">
                  {t("sleepCoachPreferences.Your Sleep Preferences")}
                </div>

                {/* Preferences List */}
                <div className="p-3 md:p-4 space-y-4 md:space-y-6">
                  {options?.length > 0 ? (
                    options?.map(({ label, key, type, options }) => {
                      if (type !== "country-select") {
                        return (
                          <div
                            key={key}
                            className="grid grid-cols-1 md:grid-cols-8 gap-3 md:gap-4 p-3 md:p-4 bg-gray-50 rounded-lg border border-gray-100"
                          >
                            {/* Field Label - Full width on mobile, 4 cols on desktop */}
                            <div className="md:col-span-4">
                              <span className="text-sm md:text-base font-medium text-gray-700">
                                {t(`sleepCoachPreferences.fields.${key}`)}
                              </span>
                            </div>

                            {/* Input/Dropdown - Full width on mobile, 2 cols on desktop */}
                            <div className="md:col-span-2">
                              <div className="w-full">
                                {type === "multi-select" ? (
                                  <MedicalViewMultipSelectDropdown
                                  isFistForm={true}
                                    options={options.map((opt) => ({
                                      value: opt,
                                      label: t(
                                        `sleepCoachPreferences.options.${key}.${opt}`
                                      ),
                                    }))}
                                    selectedValues={selectedTags[key] || []}
                                    onSelect={(selectedOptions) => {
                                      const originalValues = selectedOptions.map(
                                        (opt) =>
                                          typeof opt === "object"
                                            ? opt.value
                                            : opt
                                      );
                                      handleMultiSelectChange(
                                        key,
                                        originalValues
                                      );
                                    }}
                                    hasError={errors[key]}
                                    label={t(
                                      `sleepCoachPreferences.fields.${key}`
                                    )}
                                    placeholder={t(
                                      "preferences.validation.onlyOne"
                                    )}
                                    isEditable={true}
                                  />
                                ) : type === "select" ? (
                                  <>
                                    <select
                                      className={`w-full border p-2 rounded-md h-10 text-sm outline-none ${
                                        errors[key]
                                          ? "border-red-500 text-red-500 bg-white"
                                          : "text-black bg-white"
                                      }`}
                                      value={
                                        Array.isArray(selectedTags[key])
                                          ? selectedTags[key][
                                              selectedTags[key].length - 1
                                            ]
                                          : selectedTags[key] || ""
                                      }
                                      onChange={(e) =>
                                        handleSelectChange(key, e.target.value)
                                      }
                                    >
                                      <option value="" disabled>
                                        {t("preferences.validation.onlyOne")}
                                      </option>
                                      {options.map((option) => (
                                        <option
                                          key={option}
                                          value={option}
                                          className="text-black"
                                        >
                                          {t(
                                            `sleepCoachPreferences.options.${key}.${option}`
                                          )}
                                        </option>
                                      ))}
                                    </select>
                                    {errors[key] && (
                                      <div className="text-red-500 text-xs mt-1 ml-1">
                                        {errors[key]}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  ""
                                )}
                              </div>
                            </div>

                            {/* Selected Tags - Full width on mobile, 2 cols on desktop */}
                            <div className="md:col-span-2">
                              <div className="flex flex-wrap gap-2 min-h-[40px] items-start">
                                {type !== "numeric" && (
                                  <>
                                    {selectedTags[key] ? (
                                      Array.isArray(selectedTags[key]) ? (
                                        selectedTags[key]?.map((tag, index) => (
                                          <span
                                            key={tag}
                                            className="inline-flex items-center bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-xs max-w-full break-words"
                                            style={{ wordBreak: 'break-word' }}
                                          >
                                            <span className="flex-shrink min-w-0">
                                              {t(
                                                `sleepCoachPreferences.options.${key}.${tag}`
                                              )}
                                            </span>
                                            <button
                                              onClick={() =>
                                                removeTag(
                                                  key,
                                                  selectedTags[key][index]
                                                )
                                              }
                                              className="ml-1 text-blue-500 hover:text-blue-800 flex-shrink-0"
                                            >
                                              ✕
                                            </button>
                                          </span>
                                        ))
                                      ) : (
                                        <span className="inline-flex bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-xs max-w-full break-words">
                                          {t(
                                            `sleepCoachPreferences.options.${key}.${selectedTags[key]}`
                                          )}
                                        </span>
                                      )
                                    ) : (
                                      <span className="text-gray-400 text-xs">
                                        {t("preferences.noOptionSelected")}
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      } else {
                        return null;
                      }
                    })
                  ) : (
                    <div className="text-center py-8">
                      <NotFound message="No Preferences Found." />
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-end items-center gap-3 mt-6 md:mt-8">
                <button
                  onClick={() => navigate(-1)}
                  className={`w-full sm:w-auto px-6 py-3 bg-gray-500 text-white rounded-full text-sm font-medium transition-colors hover:bg-gray-600 ${
                    isLoading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={isLoading}
                >
                  {t("preferences.buttons.cancel")}
                </button>

                {options?.length > 0 && (
                  <button
                    onClick={handleClickOpen}
                    className={`w-full sm:w-auto px-6 py-3 bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-full text-sm font-medium transition-colors hover:from-blue-700 hover:to-blue-500 ${
                      isLoading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <ButtonLoader />
                    ) : (
                      t("preferences.buttons.confirm")
                    )}
                  </button>
                )}
              </div>
            </div>

            <AlertDialog
              open={open}
              handleClickOpen={handleClickOpen}
              handleClose={handleClose}
              isLoading={isLoading}
              heading={t("preferences.dialog.heading")}
              message={t(
                "Your customized Sleep Coach plan will be created based on these preferences. Confirm to proceed."
              )}
            />
          </>
        )}
      </>
    );
  }
};

export default SleepCoachPreferencesSelection;